import React, { useState } from 'react';
import { Box, Button, Collapse, Divider, Grid, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import Bloco from './componentes/Bloco';
import Colunas from './componentes/Colunas';
import TituloDaTela from './componentes/TituloDaTela';
import BlocoBloqueado from './componentes/BlocoBloqueado';
import NumeroProtagonista from './componentes/NumeroProtagonista';
import ListaDeIndicadores from './componentes/ListaDeIndicadores';
import { ABAS } from './abas';
import { SECOES } from '../diagnostico/catalogo';
import {
  estadoDaSecao,
  montarSecao,
  montarIndicador,
  decomposicaoDuPont,
  formatarPorTipo
} from './apresentacao';
import { formatarDecimal, formatarPercentual } from '../utils/series';
import { LARGURA_DE_LEITURA } from './layout';

// Mesmo motivo das outras telas: o que ja foi exibido em corpo grande nao volta
// como linha de tabela logo abaixo.
const PROTAGONISTA_DO_CUSTO = ['icr'];
const PROTAGONISTAS_DO_RETORNO = ['roe', 'roa'];

/**
 * Tela Dívida e Retorno — dois blocos com FRONTEIRA DE DADO, não de tema.
 *
 * "Dívida" era um bloco só, com metade dos indicadores exigindo balanço e
 * metade não. Ele apareceria meio preenchido, que é exatamente o que a
 * degradação por bloco inteiro existe para impedir. Agora juros, ICR e DSCR —
 * que o DRE responde sozinho — ficam num bloco que nunca fecha, e endividamento,
 * composição e retorno ficam no bloco que exige balanço.
 */
/**
 * Por que o ICR não existe neste mês.
 *
 * Espelha a frase que Caixa e Ciclo já usa para o runway ("A empresa não
 * queimou caixa no período — não há prazo a contar"): quando a ausência é boa
 * notícia, o produto diz que é boa notícia.
 */
const motivoDoIcrAusente = (valores) => {
  if (valores.icr !== null && valores.icr !== undefined) return undefined;
  const juros = valores.despesasFinanceiras;
  if (juros === 0) return 'A empresa não pagou juros no período — não há o que cobrir.';
  if (juros === null || juros === undefined) {
    return 'Requer os juros e tarifas do período informados, além do resultado operacional.';
  }
  return 'Requer o resultado operacional do período.';
};

const TelaDividaRetorno = ({ leitura, onIrPara }) => {
  const [mostrarDuPont, setMostrarDuPont] = useState(false);
  const capital = estadoDaSecao(leitura, SECOES.ESTRUTURA_CAPITAL);
  const valores = leitura?.valores || {};
  const icr = montarIndicador(leitura, 'icr');
  const duPont = decomposicaoDuPont(leitura);

  const irParaDados = () => {
    if (onIrPara) onIrPara(ABAS.DADOS);
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: LARGURA_DE_LEITURA, mx: 'auto' }}>
      <TituloDaTela
        titulo="Dívida e Retorno"
        descricao="A dívida cabe no bolso, e o dinheiro dos sócios está rendendo?"
      />
      {/* CUSTO DA DÍVIDA E ESTRUTURA DE CAPITAL LADO A LADO a partir de 1200px:
          quanto a dívida custa e quem financiou a empresa são a mesma conta
          vista dos dois lados. */}
      <Colunas apartir="lg" minimo={440} sx={{ alignItems: 'start' }}>
      <Bloco
        titulo="Custo da dívida"
        pergunta="A dívida cabe no bolso?"
        nota="Juros e tarifas apenas. A amortização do principal não é custo do mês — é devolução de dinheiro emprestado, e entra só no DSCR. Somá-la aqui derrubaria o ICR de qualquer empresa que esteja pagando um financiamento em dia."
      >
        <NumeroProtagonista
          rotulo="Cobertura de juros (ICR)"
          valor={formatarPorTipo(valores.icr, 'decimal')}
          nivel={icr?.nivel}
          rotuloDaFaixa={icr?.rotulo}
          cor={icr?.cor}
          fonte={icr?.fonte}
          leitura={
            valores.icr === null
              ? null
              : `O resultado operacional cobre ${formatarDecimal(valores.icr, 1)} vezes os juros do período.`
          }
          // AUSÊNCIA SEM MOTIVO É O DEFEITO QUE ESTE PRODUTO EXISTE PARA NÃO TER
          // — E MOTIVO ERRADO É PIOR QUE MOTIVO NENHUM.
          //
          // Com `valores.icr === null` a leitura vinha nula e o protagonista da
          // tela saía como um travessão sozinho sobre 90px de vazio. A primeira
          // correção pôs uma frase ali, e a frase estava errada: pedia "informe
          // os juros" numa empresa que informou juros de R$ 0 — o valor aparece
          // duas linhas abaixo, na própria lista. O ICR é nulo porque não há o
          // que cobrir, e não porque falta dado.
          //
          // As duas causas produzem o mesmo `null` e pedem frases opostas: numa
          // o usuário precisa lançar algo, na outra ele não precisa fazer nada.
          // Mandar quem está sem dívida procurar um dado que não existe é o
          // mesmo defeito do zero fabricado, do avesso.
          motivo={icr?.motivo || motivoDoIcrAusente(valores)}
          ajuda="Resultado operacional dividido pelos juros e tarifas do período."
        />

        <Box sx={{ mt: 3 }}>
          <ListaDeIndicadores itens={montarSecao(leitura, SECOES.CUSTO_DIVIDA, PROTAGONISTA_DO_CUSTO)} />
        </Box>
      </Bloco>

      {capital.aberta ? (
        <Bloco
          titulo="Estrutura de capital e retorno"
          pergunta="Vale a pena o capital que os sócios deixaram na empresa?"
        >
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <NumeroProtagonista
                rotulo="Retorno sobre o patrimônio (ROE)"
                valor={formatarPorTipo(valores.roe, 'percentual')}
                leitura={
                  valores.roe === null
                    ? null
                    : `Ao mês, sobre o capital dos sócios. Compare com o que esse dinheiro renderia na renda fixa no mesmo prazo.`
                }
                motivo={montarIndicador(leitura, 'roe')?.motivo}
                ajuda="Lucro médio mensal dividido pelo patrimônio líquido."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumeroProtagonista
                rotulo="Retorno sobre o ativo (ROA)"
                valor={formatarPorTipo(valores.roa, 'percentual')}
                leitura={
                  valores.roa === null
                    ? null
                    : 'Ao mês, sobre tudo que a empresa tem — independente de quem financiou.'
                }
                motivo={montarIndicador(leitura, 'roa')?.motivo}
                ajuda="Lucro médio mensal dividido pelo ativo total."
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5, maxWidth: '75ch' }}>
            O ROE não tem faixa de saúde nesta versão: a régua correta é o custo de oportunidade do
            período (a taxa básica de juros), e ela ainda não é um parâmetro do sistema. Faixa
            inventada aqui classificaria como boa uma empresa que rende menos que renda fixa.
          </Typography>

          {duPont && (
            <Box sx={{ mt: 2 }}>
              <Button
                size="small"
                onClick={() => setMostrarDuPont((atual) => !atual)}
                endIcon={mostrarDuPont ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={mostrarDuPont}
                sx={{ ml: -1 }}
              >
                {mostrarDuPont ? 'Esconder a decomposição' : 'Ver a decomposição do retorno'}
              </Button>

              <Collapse in={mostrarDuPont} unmountOnExit>
                <Box sx={{ mt: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  {duPont.confere ? (
                    <>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 1.5, sm: 3 }}
                        alignItems={{ sm: 'center' }}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Margem líquida
                          </Typography>
                          <Typography className="valor-numerico" variant="subtitle1">
                            {formatarPercentual(duPont.margem, 1)}
                          </Typography>
                        </Box>
                        <Typography color="text.secondary">×</Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Giro do ativo (mensal)
                          </Typography>
                          <Typography className="valor-numerico" variant="subtitle1">
                            {formatarDecimal(duPont.giro, 2)}
                          </Typography>
                        </Box>
                        <Typography color="text.secondary">×</Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="p">
                            Multiplicador (Ativo ÷ PL)
                          </Typography>
                          <Typography className="valor-numerico" variant="subtitle1">
                            {formatarDecimal(duPont.multiplicador, 2)}
                          </Typography>
                        </Box>
                        <Typography color="text.secondary">=</Typography>
                        <Box>
                          <Typography variant="caption" color="text.secondary" component="p">
                            ROE
                          </Typography>
                          <Typography className="valor-numerico" variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {formatarPercentual(duPont.roeCalculado, 1)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
                        Ler da esquerda para a direita responde de onde vem o retorno: de vender com
                        margem, de girar o ativo rápido, ou de usar dinheiro de terceiros.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
                      A decomposição não fecha com o ROE calculado ({formatarPercentual(duPont.roeDoMotor, 1)}{' '}
                      contra {formatarPercentual(duPont.roeCalculado, 1)}), o que indica que os fatores
                      estão sobre bases de período diferentes. Exibir uma decomposição que não
                      multiplica de volta seria um erro com cara de rigor — então ela fica oculta até
                      as bases coincidirem.
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />
          <ListaDeIndicadores itens={montarSecao(leitura, SECOES.ESTRUTURA_CAPITAL, PROTAGONISTAS_DO_RETORNO)} />
        </Bloco>
      ) : (
        <BlocoBloqueado
          titulo="Estrutura de capital e retorno"
          pergunta="Vale a pena o capital que os sócios deixaram na empresa?"
          chamada={capital.chamada}
          campos={capital.campos}
          bloqueados={capital.bloqueados}
          onDestravar={irParaDados}
        />
      )}
      </Colunas>
    </Stack>
  );
};

export default TelaDividaRetorno;
